<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <EmployeeSidebar :sidebarOpen="sidebarOpen" currentPage="advances" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Navbar -->
      <EmployeeNavbar 
        :title="$t('employee.advances.title')" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-warning">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Pending Requests</p>
                    <h3 class="mb-0 fw-bold">{{ pendingCount }}</h3>
                    <small class="text-muted">{{
                      formatCurrency(pendingAmount)
                    }}</small>
                  </div>
                  <div class="stat-icon bg-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-clock-history"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022l-.074.997zm2.004.45a7.003 7.003 0 0 0-.985-.299l.219-.976c.383.086.76.2 1.126.342l-.36.933zm1.37.71a7.01 7.01 0 0 0-.439-.27l.493-.87a8.025 8.025 0 0 1 .979.654l-.615.789a6.996 6.996 0 0 0-.418-.302zm1.834 1.79a6.99 6.99 0 0 0-.653-.796l.724-.69c.27.285.52.59.747.91l-.818.576zm.744 1.352a7.08 7.08 0 0 0-.214-.468l.893-.45a7.976 7.976 0 0 1 .45 1.088l-.95.313a7.023 7.023 0 0 0-.179-.483zm.53 2.507a6.991 6.991 0 0 0-.1-1.025l.985-.17c.067.386.106.778.116 1.17l-1 .025zm-.131 1.538c.033-.17.06-.339.081-.51l.993.123a7.957 7.957 0 0 1-.23 1.155l-.964-.267c.046-.165.086-.332.12-.501zm-.952 2.379c.184-.29.346-.594.486-.908l.914.405c-.16.36-.345.706-.555 1.038l-.845-.535zm-.964 1.205c.122-.122.239-.248.35-.378l.758.653a8.073 8.073 0 0 1-.401.432l-.707-.707z"
                      />
                      <path
                        d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0v1z"
                      />
                      <path
                        d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Approved Advances</p>
                    <h3 class="mb-0 fw-bold">{{ approvedCount }}</h3>
                    <small class="text-muted">{{
                      formatCurrency(approvedAmount)
                    }}</small>
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-check-circle-fill"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-danger">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">Outstanding Balance</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(outstandingAmount) }}
                    </h3>
                    <small class="text-muted">To be recovered</small>
                  </div>
                  <div class="stat-icon bg-danger">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-exclamation-triangle"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"
                      />
                      <path
                        d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Button -->
        <div class="row mb-4">
          <div class="col-12">
            <button
              class="btn btn-primary btn-lg"
              @click="showRequestModal = true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                class="bi bi-plus-circle me-2"
                viewBox="0 0 16 16"
              >
                <path
                  d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                />
                <path
                  d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"
                />
              </svg>
              Request New Advance
            </button>
          </div>
        </div>

        <!-- Filter -->
        <div class="row mb-4">
          <div class="col-md-3">
            <select class="form-select" v-model="statusFilter">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="recovered">Recovered</option>
            </select>
          </div>
        </div>

        <!-- Advances Table -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Request Date</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Approved Date</th>
                    <th>Recovered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="advance in filteredAdvances" :key="advance.id">
                    <td>{{ formatDate(advance.requestDate) }}</td>
                    <td class="fw-semibold">
                      {{ formatCurrency(advance.amount) }}
                    </td>
                    <td>{{ advance.reason }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="getStatusBadgeClass(advance.status)"
                      >
                        {{ advance.status }}
                      </span>
                    </td>
                    <td>
                      {{
                        advance.approvedDate
                          ? formatDate(advance.approvedDate)
                          : "-"
                      }}
                    </td>
                    <td>{{ advance.adjustedInPayroll ? "Yes" : "No" }}</td>
                    <td>
                      <div class="d-flex gap-1">
                        <button
                          class="btn btn-sm btn-outline-info"
                          @click="viewDetails(advance)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            fill="currentColor"
                            class="bi bi-eye"
                            viewBox="0 0 16 16"
                          >
                            <path
                              d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                            />
                            <path
                              d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                            />
                          </svg>
                          View
                        </button>
                        <button
                          v-if="advance.status === 'pending'"
                          class="btn btn-sm btn-outline-danger"
                          @click="cancelRequest(advance)"
                          :disabled="loading"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            fill="currentColor"
                            class="bi bi-x-circle"
                            viewBox="0 0 16 16"
                          >
                            <path
                              d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                            />
                            <path
                              d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"
                            />
                          </svg>
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="filteredAdvances.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      No advances found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Request Advance Modal -->
    <div
      class="modal fade"
      :class="{ show: showRequestModal, 'd-block': showRequestModal }"
      tabindex="-1"
      v-if="showRequestModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Request Advance</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeRequestModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Amount (€)</label>
              <input
                type="number"
                class="form-control"
                v-model.number="newAdvance.amount"
                min="1"
                step="0.01"
                required
              />
            </div>
            <div class="mb-3">
              <label class="form-label">Reason</label>
              <textarea
                class="form-control"
                rows="3"
                v-model="newAdvance.reason"
                placeholder="Explain why you need this advance..."
                required
              ></textarea>
            </div>
            <div class="alert alert-info small">
              <strong>Note:</strong> Advance requests are subject to approval by
              management. Approved advances will be recovered from your next
              payroll.
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeRequestModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="submitRequest"
            >
              Submit Request
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showRequestModal }"
      v-if="showRequestModal"
    ></div>

    <!-- View Details Modal -->
    <div
      class="modal fade"
      :class="{ show: showDetailsModal, 'd-block': showDetailsModal }"
      tabindex="-1"
      v-if="showDetailsModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Advance Details</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeDetailsModal"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <p>
                <strong>Amount:</strong>
                {{ formatCurrency(selectedAdvance?.amount) }}
              </p>
              <p>
                <strong>Status:</strong>
                <span
                  class="badge"
                  :class="getStatusBadgeClass(selectedAdvance?.status)"
                >
                  {{ selectedAdvance?.status }}
                </span>
              </p>
              <p>
                <strong>Request Date:</strong>
                {{ formatDate(selectedAdvance?.requestDate) }}
              </p>
              <p v-if="selectedAdvance?.approvedDate">
                <strong>Approved Date:</strong>
                {{ formatDate(selectedAdvance?.approvedDate) }}
              </p>
              <p v-if="selectedAdvance?.rejectedDate">
                <strong>Rejected Date:</strong>
                {{ formatDate(selectedAdvance?.rejectedDate) }}
              </p>
            </div>
            <div class="mb-3">
              <strong>Reason:</strong>
              <p class="text-muted">{{ selectedAdvance?.reason }}</p>
            </div>
            <div
              v-if="selectedAdvance?.approvalComments"
              class="alert alert-success small"
            >
              <strong>Approval Comments:</strong>
              {{ selectedAdvance.approvalComments }}
            </div>
            <div
              v-if="selectedAdvance?.rejectionReason"
              class="alert alert-danger small"
            >
              <strong>Rejection Reason:</strong>
              {{ selectedAdvance.rejectionReason }}
            </div>
            <div
              v-if="selectedAdvance?.status === 'cancelled'"
              class="alert alert-secondary small"
            >
              <strong>Cancelled Date:</strong>
              {{ formatDate(selectedAdvance.cancelledDate) }}
            </div>
            <div
              v-if="selectedAdvance?.adjustedInPayroll"
              class="alert alert-success small"
            >
              This advance has been recovered from your payroll.
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeDetailsModal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showDetailsModal }"
      v-if="showDetailsModal"
    ></div>
  </div>
</template>

<script>
import axios from "axios";
import EmployeeSidebar from '@/components/EmployeeSidebar.vue';
import EmployeeNavbar from '@/components/EmployeeNavbar.vue';

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "EmployeeAdvances",
  components: {
    EmployeeSidebar,
    EmployeeNavbar
  },
  data() {
    return {
      sidebarOpen: true,
      user: { id: null, name: "", email: "", role: "employee" },
      loading: false,
      statusFilter: "all",
      showRequestModal: false,
      showDetailsModal: false,
      selectedAdvance: null,
      newAdvance: {
        amount: 0,
        reason: "",
      },
      advances: [],
    };
  },
  computed: {
    filteredAdvances() {
      if (this.statusFilter === "all") {
        return this.advances;
      }
      return this.advances.filter((a) => a.status === this.statusFilter);
    },
    pendingCount() {
      return this.advances.filter((a) => a.status === "pending").length;
    },
    pendingAmount() {
      return this.advances
        .filter((a) => a.status === "pending")
        .reduce((sum, a) => sum + a.amount, 0);
    },
    approvedCount() {
      return this.advances.filter((a) => a.status === "approved").length;
    },
    approvedAmount() {
      return this.advances
        .filter((a) => a.status === "approved")
        .reduce((sum, a) => sum + a.amount, 0);
    },
    outstandingAmount() {
      return this.advances
        .filter((a) => a.status === "approved" && !a.adjustedInPayroll)
        .reduce((sum, a) => sum + a.amount, 0);
    },
  },
  async mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }
    this.user = JSON.parse(userData);
    if (this.user.role === "admin") {
      this.$router.push("/dashboard");
      return;
    }

    await this.loadAdvances();
  },
  methods: {
    getAuthHeaders() {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        return {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        };
      }
      return {};
    },
    async loadAdvances() {
      try {
        this.loading = true;
        const response = await axios.get(
          `${API_URL}/api/advances/employee`,
          { headers: this.getAuthHeaders() }
        );
        if (response.data.success) {
          this.advances = response.data.advances.map((advance) => ({
            id: advance.id,
            employeeName: advance.employeeName,
            amount: parseFloat(advance.amount),
            reason: advance.reason,
            status: advance.status,
            requestDate: advance.requestDate,
            approvedDate: advance.approvedDate,
            rejectedDate: advance.rejectedDate,
            rejectionReason: advance.rejectionReason,
            approvalComments: advance.approvalComments,
            adjustedInPayroll: advance.adjustedInPayroll,
            cancelledDate: advance.cancelledDate,
          }));
        }
      } catch (error) {
        console.error("Load advances error:", error);
        if (error.response && error.response.status === 401) {
          alert("Session expired. Please login again.");
          localStorage.removeItem("user");
          this.$router.push("/");
        } else {
          alert("Failed to load advances. Please try again.");
        }
      } finally {
        this.loading = false;
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-GB");
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    getStatusBadgeClass(status) {
      const classes = {
        pending: "bg-warning",
        approved: "bg-success",
        rejected: "bg-danger",
        cancelled: "bg-secondary",
        recovered: "bg-info",
      };
      return classes[status] || "bg-secondary";
    },
    closeRequestModal() {
      this.showRequestModal = false;
      this.newAdvance = { amount: 0, reason: "" };
    },
    async submitRequest() {
      if (!this.newAdvance.amount || !this.newAdvance.reason.trim()) {
        alert("Please fill in all fields");
        return;
      }

      if (this.newAdvance.amount <= 0) {
        alert("Amount must be greater than zero");
        return;
      }

      try {
        this.loading = true;
        const response = await axios.post(
          `${API_URL}/api/advances`,
          {
            amount: this.newAdvance.amount,
            reason: this.newAdvance.reason,
          },
          { headers: this.getAuthHeaders() }
        );

        if (response.data.success) {
          this.closeRequestModal();
          alert("Advance request submitted successfully!");
          await this.loadAdvances();
        }
      } catch (error) {
        console.error("Submit advance error:", error);
        if (error.response && error.response.status === 401) {
          alert("Session expired. Please login again.");
          localStorage.removeItem("user");
          this.$router.push("/");
        } else if (error.response && error.response.data && error.response.data.message) {
          alert(error.response.data.message);
        } else {
          alert("Failed to submit advance request. Please try again.");
        }
      } finally {
        this.loading = false;
      }
    },
    async cancelRequest(advance) {
      if (!confirm(`Are you sure you want to cancel this advance request for ${this.formatCurrency(advance.amount)}?`)) {
        return;
      }

      try {
        this.loading = true;
        const response = await axios.put(
          `${API_URL}/api/advances/${advance.id}/cancel`,
          {},
          { headers: this.getAuthHeaders() }
        );

        if (response.data.success) {
          alert("Advance request cancelled successfully!");
          await this.loadAdvances();
        }
      } catch (error) {
        console.error("Cancel advance error:", error);
        if (error.response && error.response.status === 401) {
          alert("Session expired. Please login again.");
          localStorage.removeItem("user");
          this.$router.push("/");
        } else {
          alert("Failed to cancel advance request. Please try again.");
        }
      } finally {
        this.loading = false;
      }
    },
    viewDetails(advance) {
      this.selectedAdvance = advance;
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedAdvance = null;
    },
    handleLogout() {
      localStorage.removeItem("user");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
